import React, { useState, useEffect } from "react";
import { song삭제api, song수정api } from "../api/delete/song.change";
import { useRecoilState } from "recoil";
import { artistListState, songListState } from "../atom/atom";
import { Song, Artist } from "../types/type";
import { fetchAndSetData, fetchSongList } from "../api/fetch.api";

const EditSongPage: React.FC = () => {
  const [totalSongList, setTotalSongList] = useRecoilState(songListState);
  const [totalArtistList, setTotalArtistList] = useRecoilState(artistListState);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [editedSongData, setEditedSongData] = useState<Partial<Song>>({});
  const [message, setMessage] = useState<string>("");

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSongClick = (song: Song) => {
    setSelectedSong(song);
    setEditedSongData({ ...song });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedSongData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUpdateSong = async () => {
    if (!selectedSong) return setMessage("선택된 곡이 없습니다.");

    try {
      setMessage(selectedSong.songId + " 수정중입니다.");
      await song수정api(selectedSong.songId, editedSongData);
      setMessage("곡 정보가 성공적으로 업데이트되었습니다.");
      await fetchAndSetData(fetchSongList, setTotalSongList); // 업데이트된 목록을 다시 불러옵니다.
      setSelectedSong(null);
    } catch (error: any) {
      console.error("Error updating song:", error);
      setMessage(`오류 발생: ${error.message}`);
    }
  };

  useEffect(() => {
    if (!totalSongList.length) fetchAndSetData(fetchSongList, setTotalSongList);
  }, []);

  // Map songs by artist
  const songsByArtist = totalArtistList.reduce(
    (acc: { [key: string]: Song[] }, artist: Artist) => {
      acc[artist.artistId] = totalSongList.filter((song) =>
        song.artistIdList.includes(artist.artistId)
      );
      return acc;
    },
    {}
  );

  return (
    <div style={styles.container}>
      <h1>Song 수정 페이지</h1>
      <div style={styles.form}>
        <input
          type="text"
          placeholder="곡 제목 검색"
          value={searchTerm}
          onChange={handleSearch}
          style={styles.input}
        />
      </div>

      <div style={styles.artistContainer}>
        {Object.entries(songsByArtist).map(([artistId, songs]) => {
          const artist = totalArtistList.find((a) => a.artistId === artistId);
          const filteredSongs = songs.filter((song) =>
            song.title.toLowerCase().includes(searchTerm.toLowerCase())
          );

          if (!filteredSongs.length && searchTerm) return null;

          return (
            <div key={artistId} style={styles.artistBlock}>
              <h3>{artist?.artistName}</h3>
              <ul style={styles.songList}>
                {songs
                  .filter((song) =>
                    song.title.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((song) => (
                    <li
                      key={song.songId}
                      onClick={() => handleSongClick(song)}
                      style={styles.songItem}
                    >
                      {parseInt(song.songId, 10)}. {song.title}
                    </li>
                  ))}
              </ul>
            </div>
          );
        })}
      </div>

      {selectedSong && (
        <div style={styles.modalOverlay} onClick={() => setSelectedSong(null)}>
          <div style={styles.editForm} onClick={(e) => e.stopPropagation()}>
            <h2>곡 정보 수정</h2>
            <label>
              제목:
              <input
                type="text"
                name="title"
                value={editedSongData.title ?? ""}
                onChange={handleInputChange}
                style={styles.input}
              />
            </label>
            <label>
              아티스트:
              <input
                type="text"
                name="artistName"
                value={editedSongData.artistName ?? ""}
                onChange={handleInputChange}
                style={styles.input}
              />
            </label>
            <button onClick={handleUpdateSong} style={styles.button}>
              수정하기
            </button>
            <p>{message}</p>
          </div>
        </div>
      )}

      {message && <p style={styles.statusMessage}>{message}</p>}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    margin: "0 auto",
    padding: "20px",
    fontFamily: "'Arial', sans-serif",
    display: "flex",
    flex: 1,
    flexDirection: "column",
  },
  form: {
    marginBottom: "20px",
  },
  input: {
    padding: "10px",
    fontSize: "16px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    width: "100%",
    boxSizing: "border-box" as "border-box",
    marginBottom: "10px",
  },
  artistContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  artistBlock: {
    width: 200,
    border: "1px solid #ccc",
    margin: "10px",
    padding: "20px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
  },
  songList: {
    listStyleType: "none",
    padding: 0,
    marginTop: "10px",
  },
  songItem: {
    padding: "8px 0",
    borderBottom: "1px solid #ccc",
    cursor: "pointer",
    fontSize: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  editForm: {
    backgroundColor: "white",
    padding: "20px",
  },
  button: {
    padding: "10px",
    fontSize: "16px",
    borderRadius: "4px",
    backgroundColor: "#28a745",
    color: "#fff",
    border: "none",
    cursor: "pointer",
  },
  statusMessage: {
    marginTop: "20px",
    color: "#333",
  },
};

export default EditSongPage;
